import React, { useState } from "react";
import {
  Box,
  Button,
  MenuItem,
  Select,
  TextField,
  Typography,
  List,
  ListItem,
  ListItemText,
  Grid,
  Chip,
  Switch,
  FormControlLabel,
  CircularProgress,
  styled,
  Tooltip,
  IconButton,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import FolderOpenIcon from "@mui/icons-material/FolderOpen";
import useGetAppsQuery from "../../useQuery/useGetAppsQuery";
import useDeleteAppMutation from "../../useQuery/useDeleteAppMutation";
import { useQueryClient } from "@tanstack/react-query";

// Styled Components
const StyledButton = styled(Button)`
  padding: 15px 28px;
  border-radius: 8px;
  text-transform: capitalize;
`;

const StyledSelect = styled(Select)`
  border-radius: 5px;
  border: 1px solid #d1dbff;
  background-color: #ffffff;
  font-size: 16px;
  color: #2b3539;
  min-width: 200px;
  max-height: 300px;

  .MuiSelect-select {
    padding: 13px 15px;
  }
`;

const StyledTextField = styled(TextField)`
  .MuiOutlinedInput-root {
    border-radius: 5px;
    border: 1px solid #d1dbff;
    background-color: #ffffff;
    color: #2b3539;

    &:focus {
      border-color: #2550d1;
      box-shadow: 0 0 0 2px rgba(37, 80, 209, 0.1);
    }
  }
`;

const StyledListItem = styled(ListItem)(({ theme }) => ({
  backgroundColor: "#f8f9fc",
  marginBottom: theme.spacing(1.5),
  borderRadius: "8px",
  padding: theme.spacing(2),
  border: "1px solid rgba(0, 0, 0, 0.03)",
  transition: "all 0.2s",
  "&:hover": {
    backgroundColor: "#eef1f8",
    borderColor: "rgba(37, 80, 209, 0.1)",
  },
}));

const FormField = ({ label, tooltip, children }) => (
  <Box sx={{ mb: 3 }}>
    <Box sx={{ display: "flex", alignItems: "center", mb: 1 }}>
      <Typography color="#8089A8" fontSize="14px" fontWeight={500}>
        {label}
      </Typography>
      {tooltip && (
        <Tooltip title={tooltip}>
          <IconButton size="small" sx={{ ml: 0.5, color: "text.secondary" }}>
            <InfoOutlinedIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      )}
    </Box>
    {children}
  </Box>
);

const AppVersionManager = ({
  products = [],
  onCreateAppVersion,
  onError,
  onSuccess,
  isCreating = false,
}) => {
  const queryClient = useQueryClient();

  const [selectedProduct, setSelectedProduct] = useState("");
  const [appLocation, setAppLocation] = useState("");
  const [appVersion, setAppVersion] = useState("");
  const [isLatestVersion, setIsLatestVersion] = useState(false);

  const deleteAppMutation = useDeleteAppMutation({
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["apps", selectedProduct] });
      onSuccess("App version deleted successfully!");
    },
    onError: (error) => {
      onError("Failed to delete app version: " + (error.message || "Unknown error"));
    },
  });

  const { data: appsData, isLoading: isLoadingApps } = useGetAppsQuery(selectedProduct, {
    enabled: !!selectedProduct,
    onError: (error) =>
      onError("Failed to load app versions: " + (error.message || "Unknown error")),
  });

  const appVersions = React.useMemo(() => {
    if (!Array.isArray(appsData)) return [];

    return appsData.map((app) => ({
      id: app.appId,
      versionName: app.appVersion,
      appVersion: app.appVersion,
      appLocation: app.appLocation,
      productId: app.productId,
      productType: app.productType,
      isLatest: app.isLatestVersion === "yes",
    }));
  }, [appsData]);

  const handleCreateAppVersion = async () => {
    if (!selectedProduct || !appLocation.trim() || !appVersion.trim()) {
      onError("Please fill in all required fields");
      return;
    }

    try {
      await onCreateAppVersion({
        productId: selectedProduct,
        appLocation: appLocation.trim(),
        appVersion: appVersion.trim(),
        isLatestVersion: isLatestVersion ? "yes" : "no",
        productType: "mobile",
      });

      setAppLocation("");
      setAppVersion("");
      setIsLatestVersion(false);

      queryClient.invalidateQueries({ queryKey: ["apps", selectedProduct] });

      onSuccess("App version created successfully!");
    } catch (error) {
      onError("Failed to create app version: " + (error.message || "Unknown error"));
    }
  };

  const handleDeleteAppVersion = (appVersion, productId) => {
    deleteAppMutation.mutate({ appVersion, productId });
  };
  const renderAppVersionForm = () => (
    <Box>
      <Typography variant="h6" sx={{ mb: 3, fontWeight: 600, color: "#232d3d" }}>
        Create App Version
      </Typography>

      <FormField label="Select Product*">
        <StyledSelect
          value={selectedProduct}
          onChange={(e) => setSelectedProduct(e.target.value)}
          displayEmpty
          fullWidth
          size="small"
        >
          <MenuItem disabled value="">
            Select Product
          </MenuItem>
          {products?.map((product) => (
            <MenuItem key={product.idproducts} value={product.idproducts}>
              {product.productname}
            </MenuItem>
          ))}
        </StyledSelect>
      </FormField>

      <FormField label="App Location*">
        <StyledTextField
          fullWidth
          value={appLocation}
          onChange={(e) => setAppLocation(e.target.value)}
          placeholder="Enter the full path to your app file"
          variant="outlined"
          size="small"
        />
        <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: "block" }}>
          Enter the full path to your app file (e.g., /Users/username/apps/myapp.apk or
          /Users/username/apps/myapp.ipa)
        </Typography>
      </FormField>

      <FormField label="App Version*">
        <StyledTextField
          fullWidth
          value={appVersion}
          onChange={(e) => setAppVersion(e.target.value)}
          placeholder="E.g. 1.0.0"
          variant="outlined"
          size="small"
        />
      </FormField>

      <FormField label="Version Settings">
        <FormControlLabel
          control={
            <Switch
              checked={isLatestVersion}
              onChange={(e) => setIsLatestVersion(e.target.checked)}
              color="primary"
            />
          }
          label="Mark as latest version"
        />
        <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: "block" }}>
          If enabled, this version will be marked as the latest version for the selected product
        </Typography>
      </FormField>

      <Box sx={{ mt: 3 }}>
        <StyledButton
          onClick={handleCreateAppVersion}
          disabled={!selectedProduct || !appLocation.trim() || !appVersion.trim() || isCreating}
          startIcon={isCreating ? <CircularProgress size={20} /> : <AddIcon />}
          variant="contained"
          sx={{
            backgroundColor: "#2550d1",
            boxShadow: "0px 16px 30.6px 0px rgba(37, 80, 209, 0.27)",
            "&:hover": {
              backgroundColor: "#1e3fa1",
            },
            "&:disabled": {
              backgroundColor: "#cccccc",
              color: "#666666",
            },
          }}
        >
          {isCreating ? "Creating..." : "Create App Version"}
        </StyledButton>
      </Box>
    </Box>
  );

  const renderAppVersionsList = () => (
    <Box>
      <Typography variant="h6" sx={{ mb: 3, fontWeight: 600, color: "#232d3d" }}>
        App Versions
        {selectedProduct && (
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            {isLoadingApps ? "Loading..." : `${appVersions.length} version(s) found`}
          </Typography>
        )}
      </Typography>

      {!selectedProduct ? (
        <Box
          sx={{
            textAlign: "center",
            py: 5,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            color: "text.secondary",
            backgroundColor: "#f8f9fc",
            borderRadius: "8px",
          }}
        >
          <FolderOpenIcon sx={{ fontSize: 48, color: "rgba(0, 0, 0, 0.2)", mb: 2 }} />
          <Typography variant="subtitle1" sx={{ mb: 1, color: "text.secondary" }}>
            Select a product to view app versions
          </Typography>
          <Typography variant="body2" sx={{ maxWidth: 400 }}>
            Choose a product from the dropdown to see its available app versions
          </Typography>
        </Box>
      ) : isLoadingApps ? (
        <Box
          sx={{
            textAlign: "center",
            py: 5,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            color: "text.secondary",
            backgroundColor: "#f8f9fc",
            borderRadius: "8px",
          }}
        >
          <CircularProgress sx={{ mb: 2 }} />
          <Typography variant="subtitle1" sx={{ color: "text.secondary" }}>
            Loading app versions...
          </Typography>
        </Box>
      ) : appVersions.length > 0 ? (
        <List sx={{ px: 0 }}>
          {appVersions.map((version) => (
            <StyledListItem key={version.id}>
              <ListItemText
                primary={
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <Typography variant="subtitle1" fontWeight={600}>
                      {version.versionName}
                    </Typography>
                    {version.isLatest && (
                      <Chip
                        size="small"
                        label="Latest"
                        color="primary"
                        sx={{ borderRadius: "6px" }}
                      />
                    )}
                  </Box>
                }
                secondary={
                  <Box sx={{ mt: 1 }}>
                    <Chip
                      size="small"
                      label={`Version: ${version.appVersion}`}
                      sx={{ mr: 1, mb: 1, borderRadius: "6px" }}
                    />
                    <Chip
                      size="small"
                      label={`Type: ${version.productType}`}
                      sx={{ mr: 1, mb: 1, borderRadius: "6px" }}
                    />
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{ display: "block", mt: 1 }}
                    >
                      Location: {version.appLocation}
                    </Typography>
                    <Typography variant="caption" color="text.secondary" sx={{ display: "block" }}>
                      ID: {version.id}
                    </Typography>
                  </Box>
                }
              />
              <Box sx={{ display: "flex", alignItems: "center" }}>
                <Tooltip title="Delete app version">
                  <IconButton
                    onClick={() => handleDeleteAppVersion(version.appVersion, version.productId)}
                    disabled={deleteAppMutation.isPending}
                    color="error"
                    size="small"
                    sx={{
                      "&:hover": {
                        backgroundColor: "rgba(244, 67, 54, 0.1)",
                      },
                    }}
                  >
                    {deleteAppMutation.isPending ? <CircularProgress size={20} /> : <DeleteIcon />}
                  </IconButton>
                </Tooltip>
              </Box>
            </StyledListItem>
          ))}
        </List>
      ) : (
        <Box
          sx={{
            textAlign: "center",
            py: 5,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            color: "text.secondary",
            backgroundColor: "#f8f9fc",
            borderRadius: "8px",
          }}
        >
          <FolderOpenIcon sx={{ fontSize: 48, color: "rgba(0, 0, 0, 0.2)", mb: 2 }} />
          <Typography variant="subtitle1" sx={{ mb: 1, color: "text.secondary" }}>
            No app versions found
          </Typography>
          <Typography variant="body2" sx={{ maxWidth: 400 }}>
            Create app versions to manage different versions of your mobile applications
          </Typography>
        </Box>
      )}
    </Box>
  );

  return (
    <Grid container spacing={3}>
      <Grid item xs={12} md={6}>
        {renderAppVersionForm()}
      </Grid>
      <Grid item xs={12} md={6}>
        {renderAppVersionsList()}
      </Grid>
    </Grid>
  );
};

export default AppVersionManager;
